import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  IconButton,
  Box,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  CircularProgress,
  MenuItem,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { colors, typography, inputs } from "../../theme/designSystem";
import SavingOverlay from "../SavingOverlay";

// import { EntityData } from "../../interfaces/EntityData";
import { Entity } from "../../interfaces/adminEntities";
import { maskDocument, isValidDocument } from "../../utils/document";
import { BRAZIL_DDI } from "../../utils/phone";
import PhoneWithCountryInput from "../PhoneWithCountryInput";

interface EntityModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: Entity) => void | Promise<void>;
  mode: "create" | "edit";
  initialData?: Partial<Entity> & { id?: string };
  title?: string;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Fusos do Brasil (RN-305): o relógio da clínica; mudança de praça = entidade nova.
const TIME_ZONES = [
  { id: "America/Sao_Paulo", label: "Brasília (UTC−3)" },
  { id: "America/Manaus", label: "Amazonas (UTC−4)" },
  { id: "America/Rio_Branco", label: "Acre (UTC−5)" },
  { id: "America/Noronha", label: "Fernando de Noronha (UTC−2)" },
];

const EntityModal: React.FC<EntityModalProps> = ({
  isOpen,
  onClose,
  onSave,
  mode,
  initialData = {},
  title,
}) => {
  // default form values; don't spread initialData here to avoid overwriting while user types
  const defaultFormValues: Entity = {
    // entityType: "juridica",
    // id: "",
    // cnpjCpf: "",
    // inscricaoEstadual: "",
    // inscricaoMunicipal: "",
    // fantasyName: "",
    // socialName: "",
    // ddd: "",
    // phone: "",
    // email: "",
    // cep: "",
    // street: "",
    // number: "",
    // complement: "",
    // neighborhood: "",
    // city: "",
    // state: "",
    // startTime: "08:00",
    // endTime: "18:00",
    id: "",
    fantasyName: "",
    cnpjCpf: "",
    socialName: "",
    workingHours: "",
    entityType: "juridica",
    inscricaoEstadual: "",
    inscricaoMunicipal: "",
    ddd: "",
    phone: "",
    phoneDdi: BRAZIL_DDI,
    email: "",
    addressZipcode: "",
    addressStreet: "",
    addressNumber: "",
    addressComplement: "",
    addressNeighborhood: "",
    addressCity: "",
    addressState: "",
    startTime: "",
    endTime: "",
    timeZone: "America/Sao_Paulo",
  };

  const [formData, setFormData] = useState<Entity>(defaultFormValues);
  const [documentError, setDocumentError] = useState<string>("");
  const [emailError, setEmailError] = useState<string>("");
  const [nameError, setNameError] = useState<string>("");

  // Initialize the form only when the modal opens. This avoids overwriting the form
  // while the user is typing if the parent passes a new initialData reference.
  useEffect(() => {
    if (!isOpen) return;

    setDocumentError("");
    setEmailError("");
    setNameError("");

    if (mode === "edit" && initialData && Object.keys(initialData).length > 0) {
      console.log("Dados recebidos no modal:", initialData);

      // Extrair horário de funcionamento
      const [startTime = "08:00", endTime = "18:00"] =
        initialData.workingHours?.split(" - ") ?? [];

      console.log("InitialData no mapeamento:", initialData);

      const mappedData: Entity = {
        // Dados principais - campos obrigatórios
        id: initialData.id || "",
        fantasyName: initialData.fantasyName || "",
        cnpjCpf: maskDocument(
          initialData.cnpjCpf || "",
          initialData.entityType || "juridica"
        ),
        socialName: initialData.socialName || "",
        workingHours: initialData.workingHours || `${startTime} - ${endTime}`,
        entityType: initialData.entityType || "juridica",

        // Campos opcionais - mantendo os valores originais mesmo que vazios
        inscricaoEstadual: initialData.inscricaoEstadual || "",
        inscricaoMunicipal: initialData.inscricaoMunicipal || "",

        // Campos de contato - mantendo os valores originais mesmo que vazios
        ddd: initialData.ddd || "",
        phone: initialData.phone || "",
        phoneDdi: initialData.phoneDdi || BRAZIL_DDI,
        email: initialData.email || "",

        // Campos de endereço - mantendo os valores originais mesmo que vazios
        addressZipcode: initialData.addressZipcode || "",
        addressStreet: initialData.addressStreet || "",
        addressNumber: initialData.addressNumber || "",
        addressComplement: initialData.addressComplement || "",
        addressNeighborhood: initialData.addressNeighborhood || "",
        addressCity: initialData.addressCity || "",
        addressState: initialData.addressState || "",

        // Campos de horário - usando os valores padrão apenas se necessário
        startTime: startTime || "08:00",
        endTime: endTime || "18:00",
        timeZone: initialData.timeZone || "America/Sao_Paulo",
        financialCutoffDate: initialData.financialCutoffDate || "",
      };

      setFormData(mappedData);
      return;
    }

    // create mode: reset to defaults when opening
    if (mode === "create") {
      setFormData(defaultFormValues);
    }
    // intentionally not including initialData in deps to avoid overwriting while open
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, mode]);

  const handleClose = () => {
    setFormData({
      entityType: "juridica",
      id: "",
      cnpjCpf: "",
      inscricaoEstadual: "",
      inscricaoMunicipal: "",
      fantasyName: "",
      socialName: "",
      ddd: "",
      phone: "",
      phoneDdi: BRAZIL_DDI,
      email: "",
      addressZipcode: "",
      addressStreet: "",
      addressNumber: "",
      addressComplement: "",
      addressNeighborhood: "",
      addressCity: "",
      addressState: "",
      workingHours: "08:00 - 18:00",
      startTime: "08:00",
      endTime: "18:00",
      timeZone: "America/Sao_Paulo",
    });
    setDocumentError("");
    setEmailError("");
    setNameError("");
    onClose();
  };

  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (saving) return;
    const isFisica = formData.entityType === "fisica";

    // Documento (CPF ou CNPJ) é obrigatório e precisa ser válido (dígitos verificadores).
    const documentDigits = (formData.cnpjCpf ?? "").replace(/\D/g, "");
    if (!documentDigits) {
      setDocumentError(isFisica ? "CPF obrigatório" : "CNPJ obrigatório");
      return;
    }
    if (!isValidDocument(documentDigits, formData.entityType)) {
      setDocumentError(isFisica ? "CPF inválido" : "CNPJ inválido");
      return;
    }
    setDocumentError("");

    // Nome é obrigatório (nome fantasia para PJ, nome da pessoa para PF).
    if (!(formData.fantasyName ?? "").trim()) {
      setNameError(isFisica ? "Nome obrigatório" : "Nome fantasia obrigatório");
      return;
    }
    setNameError("");

    // Valida o formato do e-mail (quando informado) antes de gravar.
    const email = (formData.email ?? "").trim();
    if (email && !EMAIL_REGEX.test(email)) {
      setEmailError("E-mail inválido");
      return;
    }
    setEmailError("");

    // Ponto unico de gravacao: o onSave (AdminEntities.handleSaveEntity) monta o payload,
    // chama a API, mostra o toast, atualiza a lista e fecha o modal. Evita gravacao dupla.
    setSaving(true);
    try {
      await onSave(formData);
    } finally {
      setSaving(false);
    }
  };

  // ViaCEP: ao informar um CEP valido (8 digitos), preenche logradouro, bairro, cidade e UF.
  // Servico publico e gratuito; falha de rede mantem os campos para digitacao manual.
  const fillAddressByCep = async (rawCep: string) => {
    const cep = rawCep.replace(/\D/g, "");
    if (cep.length !== 8) return;

    try {
      const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
      if (!response.ok) return;

      const data = await response.json();
      if (data.erro) return;

      setFormData((prev) => ({
        ...prev,
        addressStreet: data.logradouro || prev.addressStreet,
        addressNeighborhood: data.bairro || prev.addressNeighborhood,
        addressCity: data.localidade || prev.addressCity,
        addressState: data.uf || prev.addressState,
      }));
    } catch {
      // CEP indisponivel (rede/servico fora): mantem os valores atuais.
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={(_, reason) => {
        if (saving) return;
        if (reason === "backdropClick" || reason === "escapeKeyDown") {
          handleClose();
          return;
        }
        handleClose();
      }}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "12px",
          boxShadow: "0 10px 30px rgba(0,0,0,0.3)",
          maxHeight: "90vh",
          position: "relative",
          overflow: "hidden",
        },
      }}
    >
      <SavingOverlay open={saving} />
      <DialogTitle
        sx={{
          backgroundColor: colors.primary,
          color: colors.white,
          padding: "1.5rem",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography
          variant="h6"
          component="h3"
          sx={{
            fontSize: "1.4rem",
            fontWeight: typography.fontWeight.semibold,
            margin: 0,
          }}
        >
          {title ||
            (mode === "create" ? "Cadastrar Entidade" : "Editar Entidade")}
        </Typography>
        <IconButton
          onClick={handleClose}
          disabled={saving}
          sx={{
            color: colors.white,
            padding: "0.25rem",
            "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.1)" },
            "&.Mui-disabled": { color: "rgba(255,255,255,0.4)" },
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ padding: "1.5rem", paddingTop: "calc(1.5rem + 15px)" }}>
        <FormControl component="fieldset" sx={{ marginBottom: "0.75rem" }}>
          <RadioGroup
            row
            value={formData.entityType}
            onChange={(e) => {
              const newType = e.target.value as "juridica" | "fisica";
              // Troca de tipo zera o documento (CPF<->CNPJ não se reaproveitam);
              // Pessoa Física também não tem inscrições nem razão social.
              setFormData({
                ...formData,
                entityType: newType,
                cnpjCpf: "",
                inscricaoEstadual:
                  newType === "fisica" ? "" : formData.inscricaoEstadual,
                inscricaoMunicipal:
                  newType === "fisica" ? "" : formData.inscricaoMunicipal,
                // Pessoa Física não tem razão social.
                socialName: newType === "fisica" ? "" : formData.socialName,
              });
              setDocumentError("");
            }}
          >
            <FormControlLabel
              value="juridica"
              control={
                <Radio
                  sx={{
                    color: colors.primary,
                    "&.Mui-checked": { color: colors.primary },
                  }}
                />
              }
              label="Pessoa Jurídica"
              sx={{
                "& .MuiFormControlLabel-label": {
                  fontSize: "0.95rem",
                  color: colors.text,
                },
              }}
            />
            <FormControlLabel
              value="fisica"
              control={
                <Radio
                  sx={{
                    color: colors.primary,
                    "&.Mui-checked": { color: colors.primary },
                  }}
                />
              }
              label="Pessoa Física"
              sx={{
                "& .MuiFormControlLabel-label": {
                  fontSize: "0.95rem",
                  color: colors.text,
                },
              }}
            />
          </RadioGroup>
        </FormControl>

        <TextField
          label={formData.entityType === "juridica" ? "CNPJ" : "CPF"}
          value={formData.cnpjCpf}
          onChange={(e) => {
            setFormData({
              ...formData,
              cnpjCpf: maskDocument(e.target.value, formData.entityType),
            });
            if (documentError) setDocumentError("");
          }}
          onBlur={(e) => {
            const digits = e.target.value.replace(/\D/g, "");
            if (digits && !isValidDocument(digits, formData.entityType)) {
              setDocumentError(
                formData.entityType === "fisica" ? "CPF inválido" : "CNPJ inválido"
              );
            }
          }}
          error={!!documentError}
          helperText={documentError}
          placeholder={formData.entityType === "juridica" ? "CNPJ" : "CPF"}
          fullWidth
          margin="dense"
          inputProps={{ maxLength: formData.entityType === "juridica" ? 18 : 14 }}
          InputLabelProps={{ shrink: true }}
          sx={{
            marginBottom: "1.5rem",
            "& .MuiOutlinedInput-root": {
              height: inputs.default.height,
              "& fieldset": { borderColor: colors.border },
              "&:hover fieldset": { borderColor: colors.border },
              "&.Mui-focused fieldset": { borderColor: colors.primary },
            },
            "& .MuiInputLabel-root": {
              fontSize: inputs.default.labelFontSize,
              color: colors.textSecondary,
              backgroundColor: colors.white,
              padding: inputs.default.labelPadding,
              "&.Mui-focused": { color: colors.primary },
            },
          }}
        />

        {formData.entityType === "juridica" && (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "0.75rem",
              marginBottom: "1.5rem",
            }}
          >
            <TextField
              label="Insc. Estadual"
            value={formData.inscricaoEstadual}
            onChange={(e) =>
              setFormData({ ...formData, inscricaoEstadual: e.target.value })
            }
            placeholder="Insc. Estadual"
            inputProps={{ maxLength: 20 }}
            InputLabelProps={{ shrink: true }}
            sx={{
              "& .MuiOutlinedInput-root": {
                height: inputs.default.height,
                "& fieldset": { borderColor: colors.border },
                "&:hover fieldset": { borderColor: colors.border },
                "&.Mui-focused fieldset": { borderColor: colors.primary },
              },
              "& .MuiInputLabel-root": {
                fontSize: inputs.default.labelFontSize,
                color: colors.textSecondary,
                backgroundColor: colors.white,
                padding: inputs.default.labelPadding,
                "&.Mui-focused": { color: colors.primary },
              },
            }}
          />
          <TextField
            label="Insc. Municipal"
            value={formData.inscricaoMunicipal}
            onChange={(e) =>
              setFormData({ ...formData, inscricaoMunicipal: e.target.value })
            }
            placeholder="Insc. Municipal"
            inputProps={{ maxLength: 20 }}
            InputLabelProps={{ shrink: true }}
            sx={{
              "& .MuiOutlinedInput-root": {
                height: inputs.default.height,
                "& fieldset": { borderColor: colors.border },
                "&:hover fieldset": { borderColor: colors.border },
                "&.Mui-focused fieldset": { borderColor: colors.primary },
              },
              "& .MuiInputLabel-root": {
                fontSize: inputs.default.labelFontSize,
                color: colors.textSecondary,
                backgroundColor: colors.white,
                padding: inputs.default.labelPadding,
                "&.Mui-focused": { color: colors.primary },
              },
            }}
          />
        </Box>
        )}

        <TextField
          label={formData.entityType === "fisica" ? "Nome" : "Nome Fantasia"}
          value={formData.fantasyName}
          onChange={(e) => {
            setFormData({ ...formData, fantasyName: e.target.value });
            if (nameError) setNameError("");
          }}
          error={!!nameError}
          helperText={nameError}
          placeholder={
            formData.entityType === "fisica" ? "Nome" : "Nome Fantasia"
          }
          inputProps={{ maxLength: 100 }}
          fullWidth
          margin="dense"
          InputLabelProps={{ shrink: true }}
          sx={{
            marginBottom: "1.5rem",
            "& .MuiOutlinedInput-root": {
              height: inputs.default.height,
              "& fieldset": { borderColor: colors.border },
              "&:hover fieldset": { borderColor: colors.border },
              "&.Mui-focused fieldset": { borderColor: colors.primary },
            },
            "& .MuiInputLabel-root": {
              fontSize: inputs.default.labelFontSize,
              color: colors.textSecondary,
              backgroundColor: colors.white,
              padding: inputs.default.labelPadding,
              "&.Mui-focused": { color: colors.primary },
            },
          }}
        />

        {formData.entityType === "juridica" && (
          <TextField
            label="Razão Social"
            value={formData.socialName}
            onChange={(e) =>
              setFormData({ ...formData, socialName: e.target.value })
            }
            placeholder="Razão Social"
            inputProps={{ maxLength: 100 }}
            fullWidth
            margin="dense"
            InputLabelProps={{ shrink: true }}
            sx={{
              marginBottom: "1.5rem",
              "& .MuiOutlinedInput-root": {
                height: inputs.default.height,
                "& fieldset": { borderColor: colors.border },
                "&:hover fieldset": { borderColor: colors.border },
                "&.Mui-focused fieldset": { borderColor: colors.primary },
              },
              "& .MuiInputLabel-root": {
                fontSize: inputs.default.labelFontSize,
                color: colors.textSecondary,
                backgroundColor: colors.white,
                padding: inputs.default.labelPadding,
                "&.Mui-focused": { color: colors.primary },
              },
            }}
          />
        )}

        <PhoneWithCountryInput
          label="Telefone"
          splitDdd
          dddWidth={100}
          value={{ ddi: formData.phoneDdi || BRAZIL_DDI, ddd: formData.ddd ?? "", number: formData.phone ?? "" }}
          onChange={(phone) => setFormData({ ...formData, phoneDdi: phone.ddi, ddd: phone.ddd, phone: phone.number })}
          gap={1.5}
          sx={{ marginBottom: "1.5rem" }}
          labelProps={{ shrink: true }}
          fieldSx={{
            "& .MuiOutlinedInput-root": {
              height: inputs.default.height,
              "& fieldset": { borderColor: colors.border },
              "&:hover fieldset": { borderColor: colors.border },
              "&.Mui-focused fieldset": { borderColor: colors.primary },
            },
            "& .MuiInputLabel-root": {
              fontSize: inputs.default.labelFontSize,
              color: colors.textSecondary,
              backgroundColor: colors.white,
              padding: inputs.default.labelPadding,
              "&.Mui-focused": { color: colors.primary },
            },
          }}
        />

        <TextField
          label="E-mail"
          type="email"
          value={formData.email}
          onChange={(e) => {
            setFormData({ ...formData, email: e.target.value });
            if (emailError) setEmailError("");
          }}
          onBlur={(e) => {
            const value = e.target.value.trim();
            if (value && !EMAIL_REGEX.test(value)) {
              setEmailError("E-mail inválido");
            }
          }}
          error={!!emailError}
          helperText={emailError}
          placeholder="E-mail"
          inputProps={{ maxLength: 100 }}
          fullWidth
          margin="dense"
          InputLabelProps={{ shrink: true }}
          sx={{
            marginBottom: "1.5rem",
            "& .MuiOutlinedInput-root": {
              height: inputs.default.height,
              "& fieldset": { borderColor: colors.border },
              "&:hover fieldset": { borderColor: colors.border },
              "&.Mui-focused fieldset": { borderColor: colors.primary },
            },
            "& .MuiInputLabel-root": {
              fontSize: inputs.default.labelFontSize,
              color: colors.textSecondary,
              backgroundColor: colors.white,
              padding: inputs.default.labelPadding,
              "&.Mui-focused": { color: colors.primary },
            },
          }}
        />

        <TextField
          label="CEP"
          value={formData.addressZipcode}
          onChange={(e) => {
            const value = e.target.value;
            setFormData({ ...formData, addressZipcode: value });
            if (value.replace(/\D/g, "").length === 8) {
              fillAddressByCep(value);
            }
          }}
          onBlur={(e) => fillAddressByCep(e.target.value)}
          placeholder="CEP"
          margin="dense"
          inputProps={{ maxLength: 9 }}
          InputLabelProps={{ shrink: true }}
          sx={{
            marginBottom: "1.5rem",
            maxWidth: "200px",
            "& .MuiOutlinedInput-root": {
              height: inputs.default.height,
              "& fieldset": { borderColor: colors.border },
              "&:hover fieldset": { borderColor: colors.border },
              "&.Mui-focused fieldset": { borderColor: colors.primary },
            },
            "& .MuiInputLabel-root": {
              fontSize: inputs.default.labelFontSize,
              color: colors.textSecondary,
              backgroundColor: colors.white,
              padding: inputs.default.labelPadding,
              "&.Mui-focused": { color: colors.primary },
            },
          }}
        />

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 100px",
            gap: "0.75rem",
            marginBottom: "1.5rem",
          }}
        >
          <TextField
            label="Logradouro (Rua, Avenida, etc.)"
            value={formData.addressStreet}
            onChange={(e) =>
              setFormData({ ...formData, addressStreet: e.target.value })
            }
            placeholder="Logradouro (Rua, Avenida, etc.)"
            inputProps={{ maxLength: 100 }}
            InputLabelProps={{ shrink: true }}
            sx={{
              "& .MuiOutlinedInput-root": {
                height: inputs.default.height,
                "& fieldset": { borderColor: colors.border },
                "&:hover fieldset": { borderColor: colors.border },
                "&.Mui-focused fieldset": { borderColor: colors.primary },
              },
              "& .MuiInputLabel-root": {
                fontSize: inputs.default.labelFontSize,
                color: colors.textSecondary,
                backgroundColor: colors.white,
                padding: inputs.default.labelPadding,
                "&.Mui-focused": { color: colors.primary },
              },
            }}
          />
          <TextField
            label="Número"
            value={formData.addressNumber}
            onChange={(e) =>
              setFormData({ ...formData, addressNumber: e.target.value })
            }
            placeholder="Nº"
            inputProps={{ maxLength: 10 }}
            InputLabelProps={{ shrink: true }}
            sx={{
              "& .MuiOutlinedInput-root": {
                height: inputs.default.height,
                "& fieldset": { borderColor: colors.border },
                "&:hover fieldset": { borderColor: colors.border },
                "&.Mui-focused fieldset": { borderColor: colors.primary },
              },
              "& .MuiInputLabel-root": {
                fontSize: inputs.default.labelFontSize,
                color: colors.textSecondary,
                backgroundColor: colors.white,
                padding: inputs.default.labelPadding,
                "&.Mui-focused": { color: colors.primary },
              },
            }}
          />
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "0.75rem",
            marginBottom: "1.5rem",
          }}
        >
          <TextField
            label="Complemento"
            value={formData.addressComplement}
            onChange={(e) =>
              setFormData({ ...formData, addressComplement: e.target.value })
            }
            placeholder="Complemento"
            inputProps={{ maxLength: 60 }}
            InputLabelProps={{ shrink: true }}
            sx={{
              "& .MuiOutlinedInput-root": {
                height: inputs.default.height,
                "& fieldset": { borderColor: colors.border },
                "&:hover fieldset": { borderColor: colors.border },
                "&.Mui-focused fieldset": { borderColor: colors.primary },
              },
              "& .MuiInputLabel-root": {
                fontSize: inputs.default.labelFontSize,
                color: colors.textSecondary,
                backgroundColor: colors.white,
                padding: inputs.default.labelPadding,
                "&.Mui-focused": { color: colors.primary },
              },
            }}
          />
          <TextField
            label="Bairro"
            value={formData.addressNeighborhood}
            onChange={(e) =>
              setFormData({ ...formData, addressNeighborhood: e.target.value })
            }
            placeholder="Bairro"
            inputProps={{ maxLength: 60 }}
            InputLabelProps={{ shrink: true }}
            sx={{
              "& .MuiOutlinedInput-root": {
                height: inputs.default.height,
                "& fieldset": { borderColor: colors.border },
                "&:hover fieldset": { borderColor: colors.border },
                "&.Mui-focused fieldset": { borderColor: colors.primary },
              },
              "& .MuiInputLabel-root": {
                fontSize: inputs.default.labelFontSize,
                color: colors.textSecondary,
                backgroundColor: colors.white,
                padding: inputs.default.labelPadding,
                "&.Mui-focused": { color: colors.primary },
              },
            }}
          />
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 100px",
            gap: "0.75rem",
            marginBottom: "1.5rem",
          }}
        >
          <TextField
            label="Cidade"
            value={formData.addressCity}
            onChange={(e) =>
              setFormData({ ...formData, addressCity: e.target.value })
            }
            placeholder="Cidade"
            inputProps={{ maxLength: 60 }}
            InputLabelProps={{ shrink: true }}
            sx={{
              "& .MuiOutlinedInput-root": {
                height: inputs.default.height,
                "& fieldset": { borderColor: colors.border },
                "&:hover fieldset": { borderColor: colors.border },
                "&.Mui-focused fieldset": { borderColor: colors.primary },
              },
              "& .MuiInputLabel-root": {
                fontSize: inputs.default.labelFontSize,
                color: colors.textSecondary,
                backgroundColor: colors.white,
                padding: inputs.default.labelPadding,
                "&.Mui-focused": { color: colors.primary },
              },
            }}
          />
          <TextField
            label="UF"
            value={formData.addressState}
            onChange={(e) =>
              setFormData({ ...formData, addressState: e.target.value })
            }
            placeholder="UF"
            inputProps={{ maxLength: 2 }}
            InputLabelProps={{ shrink: true }}
            sx={{
              "& .MuiOutlinedInput-root": {
                height: inputs.default.height,
                "& fieldset": { borderColor: colors.border },
                "&:hover fieldset": { borderColor: colors.border },
                "&.Mui-focused fieldset": { borderColor: colors.primary },
              },
              "& .MuiInputLabel-root": {
                fontSize: inputs.default.labelFontSize,
                color: colors.textSecondary,
                backgroundColor: colors.white,
                padding: inputs.default.labelPadding,
                "&.Mui-focused": { color: colors.primary },
              },
            }}
          />
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: "0.75rem",
            marginBottom: "0.5rem",
          }}
        >
          <TextField
            select
            label="Fuso horário"
            value={formData.timeZone || "America/Sao_Paulo"}
            onChange={(e) =>
              setFormData({ ...formData, timeZone: e.target.value })
            }
            helperText="Relógio da clínica para agenda e relatórios (RN-305)."
            InputLabelProps={{ shrink: true }}
            sx={{
              "& .MuiOutlinedInput-root": {
                height: inputs.default.height,
                "& fieldset": { borderColor: colors.border },
                "&:hover fieldset": { borderColor: colors.border },
                "&.Mui-focused fieldset": { borderColor: colors.primary },
              },
              "& .MuiInputLabel-root": {
                fontSize: inputs.default.labelFontSize,
                color: colors.textSecondary,
                backgroundColor: colors.white,
                padding: inputs.default.labelPadding,
                "&.Mui-focused": { color: colors.primary },
              },
            }}
          >
            {TIME_ZONES.map((tz) => (
              <MenuItem key={tz.id} value={tz.id}>
                {tz.label}
              </MenuItem>
            ))}
          </TextField>
          {mode === "edit" && (
            <TextField
              type="date"
              label="Data de corte financeira"
              value={formData.financialCutoffDate || ""}
              onChange={(e) =>
                setFormData({ ...formData, financialCutoffDate: e.target.value })
              }
              helperText="Antes desta data: sem pendência e sem pagamento (RN-543h). Vazio = sem corte."
              InputLabelProps={{ shrink: true }}
              sx={{
              "& .MuiOutlinedInput-root": {
                height: inputs.default.height,
                "& fieldset": { borderColor: colors.border },
                "&:hover fieldset": { borderColor: colors.border },
                "&.Mui-focused fieldset": { borderColor: colors.primary },
              },
              "& .MuiInputLabel-root": {
                fontSize: inputs.default.labelFontSize,
                color: colors.textSecondary,
                backgroundColor: colors.white,
                padding: inputs.default.labelPadding,
                "&.Mui-focused": { color: colors.primary },
              },
            }}
            />
          )}
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          padding: "1.5rem 2rem",
          borderTop: `1px solid ${colors.backgroundAlt}`,
          backgroundColor: colors.background,
          gap: "1rem",
        }}
      >
        <Button
          onClick={handleClose}
          disabled={saving}
          variant="outlined"
          sx={{
            padding: "0.75rem 1.5rem",
            border: `1px solid ${colors.border}`,
            borderRadius: "6px",
            backgroundColor: colors.white,
            color: colors.textSecondary,
            fontSize: "1rem",
            fontWeight: typography.fontWeight.medium,
            textTransform: "none",
            "&:hover": {
              backgroundColor: colors.background,
              borderColor: "#adb5bd",
            },
          }}
        >
          Cancelar
        </Button>
        <Button
          onClick={handleSave}
          disabled={saving}
          variant="contained"
          startIcon={
            saving ? (
              <CircularProgress size={16} sx={{ color: colors.white }} />
            ) : undefined
          }
          sx={{
            padding: "0.75rem 1.5rem",
            borderRadius: "6px",
            backgroundColor: colors.primary,
            color: colors.white,
            fontSize: "1rem",
            fontWeight: typography.fontWeight.medium,
            textTransform: "none",
            boxShadow: "none",
            "&:hover": {
              backgroundColor: "#029AAB",
              boxShadow: "none",
              transform: "translateY(-1px)",
            },
            "&.Mui-disabled": {
              backgroundColor: colors.primary,
              opacity: 0.7,
              color: colors.white,
            },
          }}
        >
          {saving ? "Processando..." : "Salvar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EntityModal;
